import { useEffect, useState } from "preact/hooks"
import { useLocation } from "wouter-preact"
import { useSearch } from "wouter-preact/use-location"
import useSWR from "swr"
import { format, parseISO } from "date-fns"
import { Loader2, Download, Settings2, X } from "lucide-react"
import { api_fetch } from "../../../api/client"
import { download_export, exactCurrencyFormatter } from "../../../utils"
import { Button } from "../../../components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { TacticAccountMapModal } from "./components/tactic_account_map_modal"
import { MONTH_END_ACCRUAL_REPORT_PATH } from "../routes"

// ── Types (mirror server MonthEndReportJson) ─────────────────────────────────

export interface month_end_row {
  account_id: string | null
  account_label: string
  planned_spending: number
  volume_based_estimate: number
  actual_spending: number
  cleared: number
  not_cleared: number
  recommended_accrual: number
}

export interface plan_detail_row {
  promotion_id: string
  promotion_name: string
  remote_promo_id: string | null
  is_contract: boolean
  start_date: string | null
  end_date: string | null
  tactic_or_fund: string | null
  account_label: string
  planned_spending: number
  volume_based_estimate: number
}

export interface line_item_row {
  deduction_id: string
  invoice_number: string
  customer_name: string | null
  posting_date: string | null
  validated_date: string | null
  account_label: string
  /** This month's share of the line (equals full_amount for posting-date lines). */
  amount: number
  /** The line's whole amount, for traceability when amount is an amortized share. */
  full_amount: number
  promotion_name: string | null
  promotion_start_date: string | null
  promotion_end_date: string | null
  /** True when the line was amortized across its promotion's date range (vs posting date). */
  bucketed_by_promotion: boolean
  promo_matched: boolean
  uncoded: boolean
}

export interface month_end_report {
  year: number
  month: number
  rows: month_end_row[]
  grand_total: month_end_row
  excluded_promotion_count: number
  deductions_missing_posting_date: number
  plan_detail: plan_detail_row[]
  line_items: line_item_row[]
}

/** Columns whose numbers can be drilled into, and which detail set backs each. */
export type drill_column = "planned_spending" | "volume_based_estimate" | "actual_spending" | "cleared" | "not_cleared"

export const PLAN_COLUMNS: drill_column[] = ["planned_spending", "volume_based_estimate"]

/** Line items backing a deduction-side cell. `account_label` of null means the grand-total row. */
export function filter_line_items(items: line_item_row[], account_label: string | null, column: drill_column): line_item_row[] {
  return items.filter((item) => {
    if (account_label !== null && item.account_label !== account_label) return false
    switch (column) {
      case "cleared":
        return item.validated_date !== null
      case "not_cleared":
        return item.validated_date === null
      case "actual_spending":
        return item.promo_matched
      default:
        return true
    }
  })
}

/** Plan rows backing a plan-side cell. `account_label` of null means the grand-total row. */
export function filter_plan_detail(rows: plan_detail_row[], account_label: string | null): plan_detail_row[] {
  return rows.filter((row) => account_label === null || row.account_label === account_label)
}

// ── Pure helpers (unit-tested) ───────────────────────────────────────────────

/** Default report month = the previous calendar month (month is 1-12). */
export function default_report_month(now: Date): { year: number; month: number } {
  const month = now.getMonth() // 0-indexed current month = 1-indexed previous month
  if (month === 0) {
    return { year: now.getFullYear() - 1, month: 12 }
  }
  return { year: now.getFullYear(), month }
}

function parse_bounded_number(value: string | null, min: number, max: number): number | null {
  if (!value) return null
  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed < min || parsed > max) return null
  return parsed
}

export function report_period_from_url(search: string, now: Date): { year: number; month: number } {
  const defaults = default_report_month(now)
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`)
  return {
    year: parse_bounded_number(params.get("year"), 2000, 2100) ?? defaults.year,
    month: parse_bounded_number(params.get("month"), 1, 12) ?? defaults.month,
  }
}

const SYNTHETIC_LABELS = ["(Unmapped)", "(Uncoded)"]

/** Alphabetical by account label with the synthetic (Unmapped)/(Uncoded) rows pinned last. */
export function sort_report_rows(rows: month_end_row[]): month_end_row[] {
  return [...rows].sort((a, b) => {
    const pin_a = SYNTHETIC_LABELS.indexOf(a.account_label)
    const pin_b = SYNTHETIC_LABELS.indexOf(b.account_label)
    if (pin_a !== pin_b) return pin_a - pin_b
    return a.account_label.toLowerCase().localeCompare(b.account_label.toLowerCase())
  })
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]

// ── Component ────────────────────────────────────────────────────────────────

const COLUMNS: { key: keyof month_end_row; label: string; drill: boolean }[] = [
  { key: "planned_spending", label: "Planned Spending", drill: true },
  { key: "volume_based_estimate", label: "Volume Based Estimate", drill: true },
  { key: "actual_spending", label: "Matched Deductions", drill: true },
  { key: "cleared", label: "Cleared", drill: true },
  { key: "not_cleared", label: "Not Cleared", drill: true },
  { key: "recommended_accrual", label: "Recommended Accrual", drill: false },
]

const COLUMN_LABELS: Record<string, string> = Object.fromEntries(COLUMNS.map((c) => [c.key, c.label]))

interface drill_target {
  /** Account label, or null for the grand-total row. */
  account_label: string | null
  column: drill_column
}

function money(value: number) {
  return exactCurrencyFormatter(value)
}

async function download_report_xlsx(year: number, month: number) {
  await download_export(
    `/api/promotions/month-end-report/export?year=${year}&month=${month}`,
    `month_end_accrual_${year}-${String(month).padStart(2, "0")}.xlsx`
  )
}

export function MonthEndReportView({ className }: { className?: string }) {
  const [location, setLocation] = useLocation()
  const search = useSearch()
  const initial = report_period_from_url(search, new Date())
  const [year, setYear] = useState(initial.year)
  const [month, setMonth] = useState(initial.month)
  const [mapModalOpen, setMapModalOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [drill, setDrill] = useState<drill_target | null>(null)

  function toggle_drill(account_label: string | null, column: drill_column) {
    setDrill((prev) => (prev && prev.account_label === account_label && prev.column === column ? null : { account_label, column }))
  }

  function change_period(next_year: number, next_month: number) {
    setDrill(null)
    setYear(next_year)
    setMonth(next_month)
    const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`)
    params.set("year", String(next_year))
    params.set("month", String(next_month))
    setLocation(`${MONTH_END_ACCRUAL_REPORT_PATH}?${params.toString()}`, { replace: true })
  }

  useEffect(() => {
    const next_period = report_period_from_url(search, new Date())
    if (next_period.year === year && next_period.month === month) return

    setDrill(null)
    setYear(next_period.year)
    setMonth(next_period.month)
  }, [location, search])

  const current_year = new Date().getFullYear()
  const year_options = [current_year - 2, current_year - 1, current_year, current_year + 1]

  const { data, isLoading, mutate } = useSWR(["month-end-report", year, month], () =>
    api_fetch<month_end_report>("/promotions/month-end-report", { params: { year, month } })
  )
  const report = data?.ok ? data.value.data : null
  const rows = report ? sort_report_rows(report.rows) : []

  async function handle_export() {
    setExporting(true)
    setExportError(null)
    try {
      await download_report_xlsx(year, month)
    } catch {
      setExportError("Export failed — please try again")
    }
    setExporting(false)
  }

  return (
    <div class={`space-y-4 ${className ?? ""}`}>
      <div class="flex flex-wrap items-center gap-3">
        <h4 class="text-lg font-semibold">Month-End Accrual Report</h4>
        <div class="flex items-center gap-2 ml-auto">
          <Select value={String(month)} onValueChange={(value: string) => change_period(year, Number(value))}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTH_NAMES.map((name, idx) => (
                <SelectItem key={name} value={String(idx + 1)}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(value: string) => change_period(Number(value), month)}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {year_options.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setMapModalOpen(true)}>
            <Settings2 className="h-4 w-4 mr-2" />
            Map Tactics
          </Button>
          <Button onClick={handle_export} disabled={exporting || !report}>
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Export XLSX
          </Button>
        </div>
      </div>

      {exportError && <div class="text-sm text-red-600">{exportError}</div>}

      {isLoading && (
        <div class="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          Loading report…
        </div>
      )}

      {!isLoading && !report && <div class="text-sm text-red-600 py-8">Failed to load the month-end report.</div>}

      {!isLoading && report && (
        <>
          <div class="overflow-x-auto border rounded-lg">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-gray-50 border-b">
                  <th class="text-left font-medium text-gray-600 px-3 py-2 whitespace-nowrap">Ledger Account</th>
                  {COLUMNS.map((col) => (
                    <th key={col.key} class="text-right font-medium text-gray-600 px-3 py-2 whitespace-nowrap">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} class="px-3 py-8 text-center text-gray-500">
                      No plan or deduction activity for {MONTH_NAMES[month - 1]} {year}.
                    </td>
                  </tr>
                )}
                {rows.map((row) => {
                  const synthetic = SYNTHETIC_LABELS.includes(row.account_label)
                  const expanded = drill && drill.account_label === row.account_label
                  return (
                    <>
                      <tr key={row.account_label} class={`border-b last:border-b-0 ${synthetic ? "bg-amber-50" : ""}`}>
                        <td class={`px-3 py-2 whitespace-nowrap ${synthetic ? "text-amber-700 font-medium" : ""}`}>
                          {row.account_label}
                        </td>
                        {COLUMNS.map((col) => (
                          <ReportCell
                            key={col.key}
                            row={row}
                            col={col}
                            active={!!expanded && drill!.column === col.key}
                            onDrill={(column: drill_column) => toggle_drill(row.account_label, column)}
                          />
                        ))}
                      </tr>
                      {expanded && (
                        <tr class="border-b bg-gray-50/60">
                          <td colSpan={COLUMNS.length + 1} class="px-3 py-3">
                            <DrillDetailPanel report={report} target={drill!} onClose={() => setDrill(null)} />
                          </td>
                        </tr>
                      )}
                    </>
                  )
                })}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr class="bg-gray-50 border-t font-semibold">
                    <td class="px-3 py-2">Grand Total</td>
                    {COLUMNS.map((col) => (
                      <ReportCell
                        key={col.key}
                        row={report.grand_total}
                        col={col}
                        active={!!drill && drill.account_label === null && drill.column === col.key}
                        onDrill={(column: drill_column) => toggle_drill(null, column)}
                      />
                    ))}
                  </tr>
                  {drill && drill.account_label === null && (
                    <tr class="border-t bg-gray-50/60">
                      <td colSpan={COLUMNS.length + 1} class="px-3 py-3 font-normal">
                        <DrillDetailPanel report={report} target={drill} onClose={() => setDrill(null)} />
                      </td>
                    </tr>
                  )}
                </tfoot>
              )}
            </table>
          </div>

          <div class="space-y-1">
            {report.excluded_promotion_count > 0 && (
              <p class="text-xs text-gray-500">
                {report.excluded_promotion_count} promotion(s) excluded from plan columns (no dates/allocations or no
                planned amount).
              </p>
            )}
            {report.deductions_missing_posting_date > 0 && (
              <p class="text-xs text-gray-500">
                {report.deductions_missing_posting_date} deduction(s) invoiced this month have no posting date — they
                are included only where a split is matched to a dated promotion.
              </p>
            )}
            <p class="text-xs text-gray-400">
              Deduction amounts are amortized day-by-day across their matched promotion's date range, so each month
              bears the share of the period the money was actually for (a single-month promotion lands whole in its
              promo month; lines without promotion dates land whole in their posting month). Cleared = validated.
              Recommended Accrual = Planned Spending − Cleared — this is the accrual variance: the planned spend not
              yet settled by validated deductions. Not Cleared is informational and does not change the accrual.
            </p>
            <p class="text-xs text-gray-400">
              Matched Deductions = the portion of received deductions (validated or not) tied to a specific promotion
              on the calendar — a measure of promotion-match coverage, not part of the accrual math.
            </p>
          </div>
        </>
      )}

      <TacticAccountMapModal
        open={mapModalOpen}
        onClose={() => {
          setMapModalOpen(false)
          mutate() // Mappings may have changed — refresh the report so (Unmapped) moves.
        }}
      />
    </div>
  )
}

// ── Drill-down cells & panel ─────────────────────────────────────────────────

function ReportCell({
  row,
  col,
  active,
  onDrill,
}: {
  row: month_end_row
  col: { key: keyof month_end_row; drill: boolean }
  active: boolean
  onDrill: (column: drill_column) => void
}) {
  const value = row[col.key] as number
  if (!col.drill) {
    return <td class="px-3 py-2 text-right whitespace-nowrap tabular-nums">{money(value)}</td>
  }
  return (
    <td
      class={`px-3 py-2 text-right whitespace-nowrap tabular-nums cursor-pointer hover:bg-plue-50 ${active ? "bg-plue-100 font-medium" : ""}`}
      title="Click to see the underlying rows"
      onClick={() => onDrill(col.key as drill_column)}>
      <span class="underline decoration-dotted decoration-gray-300 underline-offset-2">{money(value)}</span>
    </td>
  )
}

const DRILL_ROW_CAP = 500

function DrillDetailPanel({ report, target, onClose }: { report: month_end_report; target: drill_target; onClose: () => void }) {
  const scope = target.account_label ?? "Grand Total"
  const is_plan = PLAN_COLUMNS.includes(target.column)
  // `?? []` guards a stale server that predates the drill-down fields.
  const plan_rows = is_plan ? filter_plan_detail(report.plan_detail ?? [], target.account_label) : []
  const line_rows = is_plan ? [] : filter_line_items(report.line_items ?? [], target.account_label, target.column)
  const shown_count = is_plan ? plan_rows.length : line_rows.length
  const total = is_plan
    ? plan_rows.reduce((sum, r) => sum + Math.round((target.column === "planned_spending" ? r.planned_spending : r.volume_based_estimate) * 100), 0) / 100
    : line_rows.reduce((sum, r) => sum + Math.round(r.amount * 100), 0) / 100

  function show_date(value: string | null) {
    return value ? format(parseISO(value), "MM/dd/yyyy") : ""
  }

  return (
    <div class="space-y-2">
      <div class="flex items-center gap-3">
        <span class="text-sm font-medium">
          {scope} — {COLUMN_LABELS[target.column]}
        </span>
        <span class="text-xs text-gray-500">
          {shown_count} row(s) · {money(total)}
          {is_plan ? " (source: promotions & contracts)" : " (source: deduction line items)"}
        </span>
        <Button variant="ghost" size="sm" className="ml-auto h-6 px-2" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div class="max-h-96 overflow-y-auto border rounded bg-white">
        {is_plan ? (
          <table class="w-full text-xs">
            <thead class="sticky top-0 bg-gray-50">
              <tr class="border-b">
                <th class="text-left font-medium text-gray-600 px-2 py-1.5">Promotion</th>
                <th class="text-left font-medium text-gray-600 px-2 py-1.5">Promo #</th>
                <th class="text-left font-medium text-gray-600 px-2 py-1.5">Type</th>
                <th class="text-left font-medium text-gray-600 px-2 py-1.5">Start</th>
                <th class="text-left font-medium text-gray-600 px-2 py-1.5">End</th>
                <th class="text-left font-medium text-gray-600 px-2 py-1.5">Tactic / Fund</th>
                <th class="text-right font-medium text-gray-600 px-2 py-1.5">Month Planned</th>
                <th class="text-right font-medium text-gray-600 px-2 py-1.5">Month Volume Est.</th>
              </tr>
            </thead>
            <tbody>
              {plan_rows.slice(0, DRILL_ROW_CAP).map((r) => (
                <tr key={`${r.promotion_id}-${r.account_label}`} class="border-b last:border-b-0">
                  <td class="px-2 py-1.5">{r.promotion_name}</td>
                  <td class="px-2 py-1.5">{r.remote_promo_id ?? ""}</td>
                  <td class="px-2 py-1.5">{r.is_contract ? "contract" : "promotion"}</td>
                  <td class="px-2 py-1.5 whitespace-nowrap">{show_date(r.start_date)}</td>
                  <td class="px-2 py-1.5 whitespace-nowrap">{show_date(r.end_date)}</td>
                  <td class="px-2 py-1.5">{r.tactic_or_fund ?? ""}</td>
                  <td class="px-2 py-1.5 text-right tabular-nums">{money(r.planned_spending)}</td>
                  <td class="px-2 py-1.5 text-right tabular-nums">{money(r.volume_based_estimate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table class="w-full text-xs">
            <thead class="sticky top-0 bg-gray-50">
              <tr class="border-b">
                <th class="text-left font-medium text-gray-600 px-2 py-1.5">Invoice #</th>
                <th class="text-left font-medium text-gray-600 px-2 py-1.5">Customer</th>
                <th class="text-left font-medium text-gray-600 px-2 py-1.5">Posting Date</th>
                <th class="text-left font-medium text-gray-600 px-2 py-1.5">Validated Date</th>
                <th class="text-right font-medium text-gray-600 px-2 py-1.5">Month Amount</th>
                <th class="text-right font-medium text-gray-600 px-2 py-1.5">Full Amount</th>
                <th class="text-left font-medium text-gray-600 px-2 py-1.5">Promotion</th>
                <th class="text-left font-medium text-gray-600 px-2 py-1.5">Promo Start</th>
                <th class="text-left font-medium text-gray-600 px-2 py-1.5">Promo End</th>
              </tr>
            </thead>
            <tbody>
              {line_rows.slice(0, DRILL_ROW_CAP).map((r, idx) => (
                <tr key={`${r.deduction_id}-${idx}`} class="border-b last:border-b-0">
                  <td class="px-2 py-1.5">
                    <a
                      href={`/deduction/${r.deduction_id}`}
                      target="_blank"
                      rel="noopener"
                      class="text-plue-700 hover:underline">
                      {r.invoice_number}
                    </a>
                    {r.uncoded && <span class="ml-1 text-amber-600">(uncoded)</span>}
                  </td>
                  <td class="px-2 py-1.5">{r.customer_name ?? ""}</td>
                  <td class="px-2 py-1.5">{show_date(r.posting_date)}</td>
                  <td class="px-2 py-1.5">{show_date(r.validated_date)}</td>
                  <td class="px-2 py-1.5 text-right tabular-nums">{money(r.amount)}</td>
                  <td class="px-2 py-1.5 text-right tabular-nums">{money(r.full_amount)}</td>
                  <td class="px-2 py-1.5">{r.promotion_name ?? ""}</td>
                  <td class="px-2 py-1.5 whitespace-nowrap">{show_date(r.promotion_start_date)}</td>
                  <td class="px-2 py-1.5 whitespace-nowrap">{show_date(r.promotion_end_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {shown_count > DRILL_ROW_CAP && (
        <p class="text-xs text-gray-500">
          Showing first {DRILL_ROW_CAP} of {shown_count} rows — export the XLSX for the full list.
        </p>
      )}
    </div>
  )
}
